import { useCallback, useEffect, useState } from 'react';

/**
 * RxAssistant layout mode preference. Three states:
 *
 *   'closed' — chat is hidden, only the floating launcher button is visible
 *   'popper' — chat renders as a floating overlay in the bottom-right
 *   'panel'  — chat docks to a dedicated right column inside PatientShell
 *
 * Persisted to localStorage (key: `rx-assistant-mode`) so the clinician's
 * preferred workflow carries across sessions and patients.
 *
 * On narrow viewports (< 1024px) we automatically downgrade `'panel'` to
 * `'popper'` — a dock would eat too much horizontal real estate on small
 * screens. The stored preference is preserved; `effectiveMode` is what the
 * UI should consult for rendering decisions, while `mode` is what the
 * toggle button should reflect (so the user's choice doesn't visually
 * "flip" the moment they resize down).
 */
export type RxAssistantMode = 'closed' | 'popper' | 'panel';

const STORAGE_KEY = 'rx-assistant-mode';
const WIDE_BREAKPOINT = '(min-width: 1024px)';

const isValidMode = (v: unknown): v is RxAssistantMode =>
  v === 'closed' || v === 'popper' || v === 'panel';

const readStored = (): RxAssistantMode => {
  if (typeof window === 'undefined') return 'closed';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (isValidMode(raw)) return raw;
  } catch {
    // localStorage may throw in private browsing — silent fall-through.
  }
  return 'closed';
};

const useIsWideViewport = (): boolean => {
  // Initialize from the current matchMedia state so first paint matches
  // reality (avoids a visible "flicker" from default → resolved value).
  const [isWide, setIsWide] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(WIDE_BREAKPOINT).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(WIDE_BREAKPOINT);
    const onChange = (e: MediaQueryListEvent) => setIsWide(e.matches);
    // `addEventListener` is the modern API; older browsers (Safari < 14) need
    // `addListener` — we use the union to stay compatible with whatever
    // the embedding environment ships.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return isWide;
};

export const useRxAssistantMode = () => {
  const [mode, setModeState] = useState<RxAssistantMode>(readStored);
  const isWide = useIsWideViewport();

  const setMode = useCallback((next: RxAssistantMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage quota / privacy mode — silent; in-memory state still works.
    }
  }, []);

  // Effective mode: caller renders against this. Stored mode stays intact so
  // the user's preference comes back when they resize back to a wide screen.
  const effectiveMode: RxAssistantMode = mode === 'panel' && !isWide ? 'popper' : mode;

  return { mode, effectiveMode, setMode, isWide };
};
